// Calculs du Prop Firm Manager Lite. Toutes les sommes passent par les helpers
// du PFM Advanced (pfmPeriod, chargeSchedule) : mêmes comptes, mêmes dates,
// mêmes montants. Rien n'est recalculé côté règles prop firm.
import { addDays, addMonths, addQuarters, addYears, endOfMonth, endOfQuarter, endOfYear, startOfMonth, startOfQuarter, startOfYear, subMonths } from "date-fns";
import {
  accountExpensesInPeriod,
  acquisitionDate,
  chargeAmountInPeriod,
  computePfmKpis,
  countsForPayouts,
  fundedDate,
  hasPropFirm,
  normalizePeriodBounds,
} from "../../../prop-firm-manager/pfmPeriod";
import { chargeCalendarInstances } from "../../../prop-firm-manager/chargeSchedule";
import { isPropFirmFundedStatus } from "../../../trading-accounts/accountStatus";
import { CLOSURE_REASON } from "../../../trading-accounts/accountClosureReason";
import { accountPricingModel, isDirectFunded } from "../../../trading-accounts/pricingModel";
import type { AccountLike, Charge } from "../../data/LiteDataProvider";

export type Phase = "all" | "challenge" | "funded";

/** Compte tel que lu ici : le compte enrichi + la date de désactivation (colonne Account). */
export type PfmAccount = AccountLike & { disabledAt?: string | Date | null };

const payoutCount = (a: PfmAccount) => a.payouts?.length ?? 0;
const isClosed = (a: PfmAccount) => !a.isActive || a.status === "inactive";

/**
 * Phase funded : statut financé, funded un jour (`wasFunded`, qui survit à la
 * clôture), passage en funded daté, ou payout déjà encaissé (compte désactivé depuis).
 */
export function isFundedPhase(a: PfmAccount): boolean {
  return isPropFirmFundedStatus(a.status, hasPropFirm(a)) || !!a.wasFunded || fundedDate(a) !== null || payoutCount(a) > 0;
}

export function matchesPhase(a: PfmAccount, phase: Phase): boolean {
  if (phase === "all") return true;
  if (!hasPropFirm(a)) return false;
  return phase === "funded" ? isFundedPhase(a) : !isFundedPhase(a);
}

/**
 * Charges du périmètre : celles des comptes retenus, plus les charges globales
 * (sans compte) quand aucun compte ni phase n'est imposé. Comme le PFM Advanced,
 * un filtre prop firm garde les charges globales (elles ne sont rattachées à
 * aucune prop firm) ; le filtre « comptes perso » les écarte.
 */
export function scopeCharges(charges: Charge[], accountIds: Set<string>, opts: { accountId: string | null; firm: string | null; phase: Phase }): Charge[] {
  const withGlobals = !opts.accountId && opts.phase === "all" && opts.firm !== "personal";
  return charges.filter(c => (c.accountId ? accountIds.has(c.accountId) : withGlobals));
}

/** KPIs du PFM Advanced sur une fenêtre (bornes incluses). */
export function kpis(accounts: PfmAccount[], charges: Charge[], from: Date | null, to: Date | null) {
  return computePfmKpis(accounts, charges, from, to);
}

export interface Breakdown { challenges: number; activations: number; recurring: number; oneOff: number; payouts: number; payoutsCount: number; total: number }

/**
 * Détail des dépenses par nature, sur exactement les mêmes règles que
 * computePfmKpis (dont le total est la somme de ces postes) : challengeCost à
 * l'achat, activationFee au passage funded, charges selon leur calendrier.
 */
export function breakdown(accounts: PfmAccount[], charges: Charge[], from: Date | null, to: Date | null): Breakdown {
  const { start, end } = normalizePeriodBounds(from, to);
  const out: Breakdown = { challenges: 0, activations: 0, recurring: 0, oneOff: 0, payouts: 0, payoutsCount: 0, total: 0 };
  const byId = new Map(accounts.map(a => [a.id, a]));
  const oldest = accounts.reduce<Date | null>((m, a) => {
    const d = a.createdAt ? new Date(a.createdAt) : new Date();
    return !m || d < m ? d : m;
  }, null) ?? new Date();

  for (const a of accounts) {
    out.challenges += accountExpensesInPeriod({ ...a, activationFee: 0 }, start, end);
    out.activations += accountExpensesInPeriod({ ...a, challengeCost: 0 }, start, end);
    if (countsForPayouts(a)) {
      for (const p of a.payouts ?? []) {
        const d = new Date(p.date).getTime();
        if ((!start || d >= start.getTime()) && (!end || d <= end.getTime())) { out.payouts += p.amount; out.payoutsCount++; }
      }
    }
  }
  for (const c of charges) {
    const owner = c.accountId ? byId.get(c.accountId) : null;
    if (c.accountId && !owner) continue;
    const since = owner ? (owner.createdAt ? new Date(owner.createdAt) : new Date()) : oldest;
    const amount = chargeAmountInPeriod(c, since, start, end);
    if (c.frequency === "one_time") out.oneOff += amount;
    else out.recurring += amount;
  }
  out.total = out.challenges + out.activations + out.recurring + out.oneOff;
  return out;
}

/** Payouts, dépenses (négatives) et net cumulé des 6 derniers mois. */
export function monthlyCashflow(accounts: PfmAccount[], charges: Charge[], monthLabel: (d: Date) => string, now = new Date()) {
  const first = startOfMonth(subMonths(now, 5));
  let cumulative = kpis(accounts, charges, null, addDays(first, -1)).netProfit;
  return Array.from({ length: 6 }, (_, i) => {
    const m = startOfMonth(subMonths(now, 5 - i));
    const k = kpis(accounts, charges, m, endOfMonth(m));
    cumulative += k.netProfit;
    return { label: monthLabel(m), payouts: k.totalPayouts, expenses: -k.totalExpenses, cumulative };
  });
}

/**
 * Taux de réussite des challenges, uniquement sur des faits enregistrés :
 * - réussi : tout compte funded, actuel ou passé (isFundedPhase), sauf les
 *   comptes achetés directement en funded. Un funded EST un challenge réussi :
 *   aucun rattachement au challenge d'origine n'est exigé (le rattachement
 *   automatique ne tourne plus en ws_only, et le manuel est rarement fait) ;
 * - raté : challenge clôturé sans aucun signe de réussite ;
 * - neutre : challenge en cours, challenge prédécesseur d'un funded, challenge
 *   clôturé `challenge_completed` ou dont la target a été atteinte. Son funded
 *   compte déjà (ou n'a pas été importé) : le compter aussi le doublerait.
 * `predecessors` = ids référencés par un previousAccountId, sur TOUS les comptes.
 *
 * Limite connue : un funded clôturé AVANT la colonne `wasFunded` (2026-10-09),
 * sans payout ni lien, a perdu son statut et compte comme un challenge raté.
 */
export function passRate(accounts: PfmAccount[], predecessors: Set<string>) {
  let passed = 0;
  let failed = 0;
  for (const a of accounts) {
    if (!hasPropFirm(a)) continue;
    if (isFundedPhase(a)) {
      if (!isDirectFunded(accountPricingModel(a))) passed++;
      continue;
    }
    if (predecessors.has(a.id) || !isClosed(a)) continue;
    if (a.deactivatedReason === CLOSURE_REASON.CHALLENGE_COMPLETED || a.targetReachedAt) continue;
    failed++;
  }
  const done = passed + failed;
  return { passed, done, rate: done ? passed / done : null };
}

export type EventKind = "payout" | "challenge" | "activation" | "recurring" | "oneOff" | "funded" | "reset" | "breached" | "passed" | "closed";

export interface PfmEvent {
  id: string;
  date: Date;
  kind: EventKind;
  /** Signé : payout positif, dépense négative. Absent pour un jalon. */
  amount?: number;
  accountId: string | null;
  /** Nom du compte, ou nom de la charge globale. */
  name: string;
}

/** Date de l'activation fee : même règle que accountExpensesInPeriod (pfmPeriod). */
function activationDate(a: PfmAccount): Date | null {
  if (a.fundedAt) return new Date(a.fundedAt);
  if (a.status === "pa" || (a.status as string) === "funded") return acquisitionDate(a);
  return null;
}

function closureKind(a: PfmAccount, breached: boolean): EventKind {
  if (a.deactivatedReason === CLOSURE_REASON.CHALLENGE_COMPLETED) return "passed";
  if (breached || a.deactivatedReason === CLOSURE_REASON.DRAWDOWN_BREACH || a.deactivatedReason === "blown") return "breached";
  return "closed";
}

/**
 * Événements du calendrier. Mêmes dates que buildAccountCalendarEvents et que
 * les KPIs, avec la nature de chaque dépense (pour des libellés traduits) ; les
 * payouts suivent countsForPayouts pour que le calendrier, le récap et les KPIs
 * donnent les mêmes totaux.
 */
export function buildEvents(accounts: PfmAccount[], charges: Charge[], breachedIds: Set<string>, now = new Date()): PfmEvent[] {
  const ev: PfmEvent[] = [];
  const names = new Map(accounts.map(a => [a.id, a.name]));
  for (const a of accounts) {
    if (countsForPayouts(a)) {
      for (const p of a.payouts ?? []) ev.push({ id: `p-${p.id}`, date: new Date(p.date), kind: "payout", amount: p.amount, accountId: a.id, name: a.name });
    }
    const bought = acquisitionDate(a);
    if (a.challengeCost && bought) ev.push({ id: `c-${a.id}`, date: bought, kind: "challenge", amount: -a.challengeCost, accountId: a.id, name: a.name });
    const act = activationDate(a);
    if (a.activationFee && act) ev.push({ id: `a-${a.id}`, date: act, kind: "activation", amount: -a.activationFee, accountId: a.id, name: a.name });
    const funded = fundedDate(a);
    if (funded) ev.push({ id: `f-${a.id}`, date: funded, kind: "funded", accountId: a.id, name: a.name });
    if (a.resetAt) ev.push({ id: `r-${a.id}`, date: new Date(a.resetAt), kind: "reset", accountId: a.id, name: a.name });
    if (isClosed(a) && a.disabledAt) ev.push({ id: `x-${a.id}`, date: new Date(a.disabledAt), kind: closureKind(a, breachedIds.has(a.id)), accountId: a.id, name: a.name });
  }
  for (const c of charges) {
    const kind: EventKind = c.frequency === "one_time" ? "oneOff" : "recurring";
    chargeCalendarInstances(c, now).forEach(({ date, amount }, i) => ev.push({
      id: `ch-${c.id}-${i}`, date, kind, amount: -amount, accountId: c.accountId ?? null,
      name: c.accountId ? `${c.name} · ${names.get(c.accountId) ?? ""}` : c.name,
    }));
  }
  return ev.sort((x, y) => y.date.getTime() - x.date.getTime());
}

export type Granularity = "month" | "quarter" | "year";

export interface PeriodRow {
  key: string;
  start: Date;
  end: Date;
  b: Breakdown;
  net: number;
}

const PERIOD_FNS: Record<Granularity, { start: (d: Date) => Date; end: (d: Date) => Date; add: (d: Date, n: number) => Date }> = {
  month: { start: startOfMonth, end: endOfMonth, add: addMonths },
  quarter: { start: startOfQuarter, end: endOfQuarter, add: addQuarters },
  year: { start: startOfYear, end: endOfYear, add: addYears },
};

/**
 * Suivi par période (mois, trimestre ou année) : une ligne par période
 * calendaire, en continu depuis la plus ancienne date connue du périmètre
 * (achat, payout, début d'une charge) jusqu'à la période courante, la plus
 * récente en tête. Chaque ligne = `breakdown` sur ses bornes, donc la somme
 * des lignes retombe sur computePfmKpis depuis le début. Une période sans
 * mouvement reste listée : un mois vide est une information.
 */
export function periodRows(accounts: PfmAccount[], charges: Charge[], granularity: Granularity, now = new Date()): PeriodRow[] {
  const fns = PERIOD_FNS[granularity];
  let first: Date | null = null;
  const consider = (d: Date | string | null | undefined) => {
    if (!d) return;
    const x = new Date(d);
    if (!Number.isNaN(x.getTime()) && (!first || x < first)) first = x;
  };
  for (const a of accounts) {
    consider(acquisitionDate(a));
    consider(fundedDate(a));
    if (countsForPayouts(a)) for (const p of a.payouts ?? []) consider(p.date);
  }
  for (const c of charges) consider(c.startDate);
  if (!first) return [];

  const rows: PeriodRow[] = [];
  const last = fns.start(now);
  for (let cur = fns.start(first); cur.getTime() <= last.getTime(); cur = fns.add(cur, 1)) {
    const end = fns.end(cur);
    const b = breakdown(accounts, charges, cur, end);
    rows.push({ key: cur.toISOString(), start: cur, end, b, net: b.payouts - b.total });
  }
  return rows.reverse();
}

/** ROI (en %) signé, ou null sans dépense. */
export const roiOf = (payouts: number, expenses: number) => (expenses > 0 ? ((payouts - expenses) / expenses) * 100 : null);
